import DigitalWarrior, { WarriorActions, WarriorStates } from "../../sprites/DigitalWarrior";
import GameScene from "../../scenes/GameScene";
import ChargePickup, { ChargePickupState } from "../../sprites/pickups/ChargePickup";

export enum AITargets {
    PLAYER = 0,
    CHARGE = 1,
    AWAY = 2 //run away from the player
}

export default class DigitalWarriorAI {

    private _aggressiveness: number;
    currentAction: WarriorActions;
    currentTarget: AITargets;
    opponent: DigitalWarrior;
    player: DigitalWarrior;
    attackHesitation: number = 0;
    defendHesitation: number = 300;
    scene: GameScene;
    bestChargeTarget: ChargePickup | null;
    awayDirection: Phaser.Math.Vector2;

    constructor(scene: GameScene, opponent: DigitalWarrior, player: DigitalWarrior){
        this.scene = scene;
        this.aggressiveness = 0.75;
        this.opponent = opponent;
        this.player = player;
        this.reset();
    }

    /*
    *   Aggressiveness will determine the likeliness off attacking vs defending when in range.
    * */
    set aggressiveness(a: number){
        if(a >= 0 && a <= 1)
            this._aggressiveness = a;
    }

    get aggressiveness(){
        return this._aggressiveness;
    }

    closeEnough(): boolean {
        return (
            this.closeEnoughX() &&
            this.closeEnoughY()
        )
    }

    closeEnoughX(){
        const target = this.getCurrentTargetObject();
        const xDiff = target.x - this.opponent.x;
        return (xDiff > -40 && xDiff < 40);
    }

    closeEnoughY(){
        const target = this.getCurrentTargetObject();
        const yDiff = target.y - this.opponent.y;
        return (yDiff > -40 && yDiff < 40);
    }

    canHitOrDefend(): boolean {
        return (
            this.currentTarget === AITargets.PLAYER &&
            this.closeEnough() &&
            this.opponent.currentAction !== WarriorActions.ATTACKING &&
            this.opponent.currentAction !== WarriorActions.DEFENDING &&
            this.opponent.currentState !== WarriorStates.HIT &&
            this.opponent.currentState !== WarriorStates.DEAD
        )
    }

    facingRightDirection(): boolean {
        const td = this.getTargetDirection();
        return (
            this.opponent.directionFacing.equals(td) ||
            this.opponent.directionFacing.y === td.y && td.x === 0 ||
            this.opponent.directionFacing.x === td.x && td.y === 0
        )
    }

    findAvailableCharge(){
        this.bestChargeTarget = null;
        this.scene.chargePickups.getChildren().forEach( (chargePickup: ChargePickup) => {
            if(
                chargePickup.active && chargePickup.pickupState === ChargePickupState.active &&
                (
                    this.bestChargeTarget === null ||
                    Phaser.Math.Distance.BetweenPoints(this.opponent,chargePickup) <
                    Phaser.Math.Distance.BetweenPoints(this.opponent,this.bestChargeTarget)
                )
            ){
                this.bestChargeTarget = chargePickup;
            }
        });
        return this.bestChargeTarget;
    }

    update(time: number, delta: number){
        //if we're hit or dead you're not allowed to think.
        if(this.opponent.currentState === WarriorStates.HIT || this.opponent.currentState === WarriorStates.DEAD) return;
        switch(this.currentAction){
            case WarriorActions.IDLE:
                //choose our target
                if(
                    this.opponent.attackCharge < this.opponent.attackCost ||
                    (this.opponent.attackCharge < this.opponent.attackCost * 2) && this.currentTarget === AITargets.CHARGE
                ){
                    if(this.findAvailableCharge() !== null)
                        this.currentTarget = AITargets.CHARGE;
                    else
                        this.currentTarget = AITargets.AWAY;
                    this.currentAction = WarriorActions.MOVING;
                    break;
                }else{
                    this.currentTarget = AITargets.PLAYER;
                }

                if(this.currentTarget === AITargets.PLAYER){
                    if(this.canHitOrDefend() && this.facingRightDirection()){
                        this.currentAction = WarriorActions.HESITATING;
                        if(
                            Math.random() < this.aggressiveness &&
                            this.opponent.canAttack() && this.player.currentAction !== WarriorActions.DEFENDING
                        ){
                            this.scene.time.delayedCall(this.attackHesitation, () => {
                                this.opponent.setAction(WarriorActions.ATTACKING);
                                this.currentAction = this.opponent.currentAction;
                            });
                        }else{
                            this.scene.time.delayedCall(this.defendHesitation, () => {
                                this.opponent.setAction(WarriorActions.DEFENDING);
                                this.currentAction = this.opponent.currentAction;
                                this.scene.time.delayedCall(1000, ()=> {
                                    this.currentAction = WarriorActions.IDLE;
                                    this.opponent.setAction(WarriorActions.IDLE);
                                });
                            });
                        }
                    }else if(!this.closeEnough() || !this.facingRightDirection()){
                        this.currentAction = WarriorActions.MOVING;
                    }else{
                        if(this.opponent.currentAction !== WarriorActions.IDLE){
                            this.currentAction = WarriorActions.IDLE;
                            this.opponent.move(new Phaser.Math.Vector2(0,0));
                        }else{
                            this.currentTarget = AITargets.AWAY;
                        }
                    }
                }
            break;
            case WarriorActions.MOVING:
                const targetDir =  this.getTargetDirection();
                this.opponent.move(targetDir);
                if(this.closeEnough() && this.facingRightDirection()){
                    this.currentAction = WarriorActions.IDLE;
                }
                if(
                    this.currentTarget === AITargets.AWAY &&
                    (this.opponent.attackCharge > this.opponent.attackCost ||
                    this.findAvailableCharge() !== null)
                ){
                    this.currentAction = WarriorActions.IDLE;
                }
            break;
            case WarriorActions.ATTACKING:
                this.currentAction = this.opponent.currentAction;
            break;
            case WarriorActions.HESITATING:
            case WarriorActions.DEFENDING:
                //timeout should be handling this.
            break;
        }
    }

    getCurrentTargetObject(){
        switch(this.currentTarget){
            case AITargets.PLAYER:
                return this.player;
            case AITargets.CHARGE:
                return this.bestChargeTarget;
            case AITargets.AWAY:
                return this.player;
            break;
        }
    }

    getTargetDirection(){
        const target = this.getCurrentTargetObject();
        const direction = new Phaser.Math.Vector2(0,0);
        if(target !== null){
            if(target.x > this.opponent.x)
                direction.x = 1;
            if(target.x < this.opponent.x)
                direction.x = -1;
            if(target.y > this.opponent.y)
                direction.y = 1;
            if(target.y < this.opponent.y)
                direction.y = -1;
        }
        if(this.currentTarget === AITargets.AWAY)
            direction.set(direction.x * -1, direction.y * -1);
        return direction;
    }

    reset(){
        this.aggressiveness = 0.30 + (Math.random()/2);
        this.currentTarget = AITargets.PLAYER;
        this.currentAction = WarriorActions.IDLE;
        this.awayDirection = new Phaser.Math.Vector2(0,0);
    }
}